import { supabase } from "../supabaseClient"

/**
 * Get the currently active tournament year
 * This drives all data throughout the app
 */
export async function getActiveYear(): Promise<number> {
  // Get all active years (there should only be one, but handle edge cases)
  const { data, error } = await supabase
    .from('years')
    .select('year, is_active')
    .eq('is_active', true)
    .order('year', { ascending: false })
  
  // DEBUG: Log what we got from the database
  console.log('[getActiveYear] Query result:', { data, error })
  
  if (error) {
    console.error('[getActiveYear] Error fetching active year:', error)
  }
  
  // If we got multiple active years (data integrity issue), use the most recent one
  if (data && data.length > 0) {
    if (data.length > 1) {
      console.warn(`[getActiveYear] Multiple active years found (${data.map(d => d.year).join(', ')}). Using ${data[0].year}.`)
    }
    console.log('[getActiveYear] Returning active year:', data[0].year)
    return data[0].year
  }
  
  // Fallback: get the most recent year in the table
  console.warn('[getActiveYear] No active year found, checking fallback...')
  const { data: fallbackData } = await supabase
    .from('years')
    .select('year')
    .order('year', { ascending: false })
    .limit(1)
  
  console.log('[getActiveYear] Fallback query result:', fallbackData)
  
  if (fallbackData && fallbackData.length > 0) {
    console.warn(`[getActiveYear] Using fallback year: ${fallbackData[0].year}`)
    return fallbackData[0].year
  }
  
  // Last resort fallback
  const currentYear = new Date().getFullYear()
  console.warn(`[getActiveYear] No years in database! Using current calendar year: ${currentYear}`)
  return currentYear
}

/**
 * Set the active tournament year (admin only)
 * Returns true if successful, false otherwise
 */
export async function setActiveYear(year: number): Promise<boolean> {
  // First, verify the year exists
  const { data: yearExists } = await supabase
    .from('years')
    .select('year')
    .eq('year', year)
    .single()
  
  if (!yearExists) {
    console.error(`Cannot set active year: year ${year} does not exist in the years table`)
    return false
  }
  
  // Deactivate all years
  const { error: deactivateError } = await supabase
    .from('years')
    .update({ is_active: false })
    .neq('year', 0)
  
  if (deactivateError) {
    console.error('Error deactivating years:', deactivateError)
    return false
  }
  
  // Activate the selected year
  const { error: activateError } = await supabase
    .from('years')
    .update({ is_active: true })
    .eq('year', year)
  
  if (activateError) {
    console.error('Error activating year:', activateError)
    return false
  }
  
  return true
}

/**
 * Get all years for admin management
 */
export async function getAllYears() {
  const { data } = await supabase
    .from('years')
    .select('*')
    .order('year', { ascending: false })
  
  return data || []
}

/**
 * Create a new year
 */
export async function createYear(year: number): Promise<boolean> {
  const { error } = await supabase
    .from('years')
    .insert({ year, is_active: false, entry_fee: 20, entries_open: false })
  
  return !error
}

